import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, User, ArrowRight, Tag, X } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { API } from "../App";
import Navbar, { AuthModal } from "../components/Navbar";
import axios from "axios";
import SEO from "../components/SEO";

const CATEGORIES = ["All", "NFL", "NBA", "WNBA", "MLB", "NHL", "Strategy", "Analysis"];

// Post Detail Modal
const PostModal = ({ post, isOpen, onClose }) => {
  if (!isOpen || !post) return null;
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-8 px-4"
        onClick={onClose}
      >
        <motion.article
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="bg-hsl-surface border border-white/10 rounded-2xl max-w-3xl w-full my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header Image */}
          <div className="relative h-64 overflow-hidden rounded-t-2xl">
            <img
              src={post.image_url || "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800"}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-hsl-surface to-transparent" />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
            <span className="absolute bottom-4 left-6 bg-hsl-primary/20 text-hsl-primary px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
              {post.category}
            </span>
          </div>
          
          {/* Content */}
          <div className="p-6 md:p-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-white mb-4">
              {post.title}
            </h1>
            
            <div className="flex items-center gap-4 text-sm text-zinc-500 mb-6 pb-6 border-b border-white/10">
              <span className="flex items-center gap-1">
                <User size={14} />
                {post.author}
              </span>
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {formatDate(post.created_at)}
              </span>
            </div>
            
            {/* Audio Player - if post has audio */}
            {post.audio_url && (
              <div className="mb-6 bg-hsl-bg/50 rounded-lg p-4 border border-white/10">
                <p className="text-zinc-400 text-sm mb-2 flex items-center gap-2">
                  <span>🎧</span> Listen to this article
                </p>
                <audio controls className="w-full" src={post.audio_url.startsWith('http') ? post.audio_url : `${window.location.origin}${post.audio_url}`}>
                  Your browser does not support the audio element.
                </audio>
              </div>
            )}
            
            {/* Post Content */}
            <div className="prose prose-invert prose-zinc max-w-none">
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">
                {post.content}
              </p>
            </div>
            
            {/* Close Button */}
            <div className="mt-8 pt-6 border-t border-white/10">
              <Button onClick={onClose} className="w-full btn-primary">
                Close Article
              </Button>
            </div>
          </div>
        </motion.article>
      </motion.div>
    </AnimatePresence>
  );
};

const BlogCard = ({ post, featured = false, onClick }) => {
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (featured) {
    return (
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-hsl-surface border border-white/5 rounded-2xl overflow-hidden card-hover group cursor-pointer"
        data-testid={`blog-featured-${post.id}`}
        onClick={() => onClick(post)}
      >
        <div className="grid md:grid-cols-2">
          <div className="relative h-64 md:h-auto overflow-hidden">
            <img
              src={post.image_url || "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800"}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-hsl-bg/90 to-transparent md:bg-gradient-to-r" />
          </div>
          <div className="p-8 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-hsl-primary/20 text-hsl-primary px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                Featured
              </span>
              <span className="bg-white/10 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                {post.category}
              </span>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-white mb-4 group-hover:text-hsl-primary transition-colors">
              {post.title}
            </h2>
            <p className="text-zinc-400 mb-6 line-clamp-3">
              {post.excerpt}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-zinc-500">
                <span className="flex items-center gap-1">
                  <User size={14} />
                  {post.author}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {formatDate(post.created_at)}
                </span>
              </div>
              <span className="text-hsl-primary font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                Read More <ArrowRight size={16} />
              </span>
            </div>
          </div>
        </div>
      </motion.article>
    );
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-hsl-surface border border-white/5 rounded-xl overflow-hidden card-hover group cursor-pointer"
      data-testid={`blog-card-${post.id}`}
      onClick={() => onClick(post)}
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={post.image_url || "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800"}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-hsl-bg/90 to-transparent" />
        <span className="absolute top-4 left-4 bg-white/10 backdrop-blur-md text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border border-white/10">
          {post.category}
        </span>
      </div>
      <div className="p-6">
        <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-3 group-hover:text-hsl-primary transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="text-zinc-400 text-sm mb-4 line-clamp-2">
          {post.excerpt}
        </p>
        <div className="flex items-center justify-between pt-4 border-t border-white/5">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Calendar size={12} />
            {formatDate(post.created_at)}
          </div>
          <span className="text-hsl-primary font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
            Read <ArrowRight size={14} />
          </span>
        </div>
      </div>
    </motion.article>
  );
};

const HammerTalkPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`, {
          params: selectedCategory !== "All" ? { category: selectedCategory } : {}
        });
        setPosts(response.data);
      } catch (error) {
        console.warn('HammerTalkPage: Failed to fetch blog posts:', error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [selectedCategory]);

  const handlePostClick = (post) => {
    setSelectedPost(post);
  };

  const featuredPost = posts[0];
  const otherPosts = posts.slice(1);

  return (
    <div className="min-h-screen bg-hsl-bg" data-testid="hammer-talk-page">
      <Navbar 
        onLoginClick={() => { setAuthMode("login"); setAuthModalOpen(true); }}
        onRegisterClick={() => { setAuthMode("register"); setAuthModalOpen(true); }}
      />
      
      <SEO 
        title="Hammer Talk - Sports Betting Blog"
        description="Expert sports betting insights, analysis, and strategy. Learn how to think like a sharp with in-depth breakdowns and betting tips."
        keywords="sports betting blog, betting strategy, sports analysis, NFL analysis, NBA analysis, betting tips, handicapping articles"
        canonical="/hammer-talk"
      />
      
      {/* Header */}
      <div className="border-b border-white/5 bg-hsl-surface/50 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white" data-testid="hammer-talk-title">
                Hammer Talk
              </h1>
              <p className="text-zinc-400 mt-2">Insights, analysis, and betting strategy</p>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filters */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <Tag size={18} className="text-zinc-500 flex-shrink-0" />
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`sport-filter-btn flex-shrink-0 ${selectedCategory === category ? 'active' : ''}`}
                data-testid={`category-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="space-y-8">
            <Skeleton className="h-96 w-full rounded-2xl" />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array(3).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-80 rounded-xl" />
              ))}
            </div>
          </div>
        ) : posts.length > 0 ? (
          <div className="space-y-12">
            {/* Featured Post */}
            {featuredPost && (
              <div data-testid="featured-section">
                <BlogCard post={featuredPost} featured onClick={handlePostClick} />
              </div>
            )}

            {/* Other Posts */}
            {otherPosts.length > 0 && (
              <div>
                <h2 className="font-heading text-2xl font-bold uppercase text-white mb-6" data-testid="latest-title">
                  Latest Articles
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {otherPosts.map((post) => (
                    <BlogCard key={post.id} post={post} onClick={handlePostClick} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20 bg-hsl-surface rounded-xl border border-white/5" data-testid="no-posts">
            <Tag className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 text-lg">No articles found</p>
            <p className="text-zinc-600 text-sm mt-1">Check back later for new content</p>
          </div>
        )}

        {/* Newsletter CTA */}
        <div className="mt-16 bg-hsl-surface border border-white/5 rounded-2xl p-8 md:p-12 text-center" data-testid="newsletter-section">
          <h2 className="font-heading text-3xl font-bold uppercase text-white mb-4">
            Stay Sharp
          </h2>
          <p className="text-zinc-400 mb-6 max-w-lg mx-auto">
            Get weekly betting insights, analysis, and exclusive content delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="input-hsl flex-1"
              data-testid="newsletter-email-input"
            />
            <button
              className="btn-primary-glow px-6 py-3 rounded-sm whitespace-nowrap"
              data-testid="newsletter-submit-btn"
            >
              Subscribe
            </button>
          </div>
          <p className="text-zinc-600 text-xs mt-4">
            No spam. Unsubscribe anytime.
          </p>
        </div>
      </div>
      
      {/* Post Detail Modal */}
      <PostModal 
        post={selectedPost} 
        isOpen={!!selectedPost} 
        onClose={() => setSelectedPost(null)} 
      />
      
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
        setMode={setAuthMode}
      />
    </div>
  );
};

export default HammerTalkPage;
